import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CodeIcon from '@mui/icons-material/Code';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuthStore } from '../stores/auth.store';
import { logout as apiLogout } from '../services/auth.service';

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { label: 'Conversions', icon: <ReceiptLongIcon />, path: '/conversions' },
  { label: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  { label: 'API Docs', icon: <CodeIcon />, path: '/api-docs' },
];

export default function Layout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const partner = useAuthStore((s) => s.partner);
  const clear = useAuthStore((s) => s.clear);

  const handleNav = (path: string) => {
    navigate(path);
    if (!isDesktop) setMobileOpen(false);
  };

  const handleLogout = async () => {
    // Server call first: only it can revoke the token and clear the httpOnly cookie.
    try { await apiLogout(); } catch { /* clear locally regardless */ }
    clear();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#0F172A',
      }}
    >
      {/* Brand header */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 4,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        />
        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
          Partner Portal
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, flex: 1 }}>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => handleNav(item.path)}
            selected={isActive(item.path)}
            sx={{
              borderRadius: 1.5,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(108, 142, 255, 0.12)',
                '&:hover': {
                  bgcolor: 'rgba(108, 142, 255, 0.18)',
                },
              },
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive(item.path) ? 600 : 400,
                color: isActive(item.path) ? 'text.primary' : 'text.secondary',
              }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Bottom section — partner info + logout */}
      <Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8125rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mr: 1,
            }}
          >
            {partner?.name ?? 'Partner'}
          </Typography>
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile AppBar */}
      {!isDesktop && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: '#0F172A',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Partner Portal
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      {isDesktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.08)',
              bgcolor: '#0F172A',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.08)',
              bgcolor: '#0F172A',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ...(isDesktop ? {} : { mt: '64px' }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
